import React, { useState, useEffect, useCallback } from 'react'

import { API, Storage } from 'aws-amplify'
import { getStaffProfile } from './../../../managers/graphql/queries'

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import InfoIcon from '@mui/icons-material/Info'

import { COLORS } from '../../../theme/muiTheme';

import { useDropzone } from 'react-dropzone'

// redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { getActiveClassyConnections, getOrganizationStaffProfiles } from '../../../managers/redux/slices/organization'
import { FundraisingCampaign, FundraisingCampaignType, Program, StaffProfile } from '../../../API';
import StaffSelector from '../../inputs/StaffSelector';
import { createBackendFundraisingCampaign, getFundraisingCampaigns, clearSelectedCampaign, getClassyCampaigns } from '../../../managers/redux/slices/fundraising';
import RichTextEditor, { EditorValue } from 'react-rte';
import FundraisingCampaignSelector from '../../inputs/FundraisingCampaignSelector';
import { reloadAuthAccount } from '../../../managers/redux/slices/authAccount';
import ClassyCampaignSelector from '../../inputs/ClassyCampaignSelector';

interface CreateFundraisingCampaignDialogProps {
    visible: boolean,
    onClose: () => void
}

export default function ManageFundraisingCampaign(props: CreateFundraisingCampaignDialogProps) {

    const [id, setId] = useState<string | null>(null)

    // form inputs
    const [type, setType] = useState<FundraisingCampaignType | null>(FundraisingCampaignType.GivingCircle)
    const [name, setName] = useState<string | null>(null)
    const [staffLead, setStaffLead] = useState<StaffProfile | null>(null)
    const [introVideoURI, setIntroVideoURI] = useState<string | null>(null)
    const [textMessageTemplate, setTextMessageTemplate] = useState<string | null>(null)
    const [initialTextReminderTemplate, setInitialTextReminderTemplate] = useState<string | null>(null)
    const [phoneCallScript, setPhoneCallScript] = useState<string | null>(null)
    const [phoneCallScriptRTE, setPhoneCallScriptRTE] = useState<EditorValue | null>(null)
    const [followUpYesMessageTemplate, setFollowUpYesMessageTemplate] = useState<string | null>(null)
    const [followUpNoMessageTemplate, setFollowUpNoMessageTemplate] = useState<string | null>(null)
    const [followUpReminderTemplate, setFollowUpReminderTemplate] = useState<string | null>(null)
    const [introVideoFile, setIntroVideoFile] = useState<any>(null)
    const [campaignGoal, setCampaignGoal] = useState<number>(0)
    const [perAmbassadorGoal, setPerAmbassadorGoal] = useState<number>(0)
    const [perContactDonationGoal, setPerContactDonationGoal] = useState<number>(0)
    const [campaignEndDate, setCampaignEndDate] = useState<string | null>(null)
    const [callToActionURL, setCallToActionURL] = useState<string | null>(null)
    const [crowbarVideoURL, setCrowbarVideoURL] = useState<string | null>(null)
    const [givingCircleBaseAmount, setGivingCircleBaseAmount] = useState<string>("25")
    const [givingCircleTierOneMultiplier, setGivingCircleTierOneMultiplier] = useState<string>("1")
    const [givingCircleTierTwoMultiplier, setGivingCircleTierTwoMultiplier] = useState<string>("2")
    const [givingCircleTierThreeMultiplier, setGivingCircleTierThreeMultiplier] = useState<string>("4")
    const [givingCircleTierFourMultiplier, setGivingCircleTierFourMultiplier] = useState<string>("10")
    const [givingCircleAllowCustomAmounts, setGivingCircleAllowCustomAmounts] = useState<boolean>(true)
    const [ambassadorSpecificCtaUrlEnabled, setAmbassadorSpecificCtaUrlEnabled] = useState<boolean>(false)
    const [ambassadorCanUploadCtaUrl, setAmbassadorCanUploadCtaUrl] = useState<boolean>(false)
    const [ambassadorSpecificVideoUrlEnabled, setAmbassadorSpecificVideoUrlEnabled] = useState<boolean>(false)
    const [ambassadorCanUploadVideoUrl, setAmbassadorCanUploadVideoUrl] = useState<boolean>(false)
    const [recruitmentForCampaign, setRecruitmentForCampaign] = useState<FundraisingCampaign | null>(null)
    const [useClassyCtaURL, setUseClassyCtaURL] = useState<boolean>(false)
    const [classyDonationPageHostURL, setClassyDonationPageHostUrL] = useState<string | null>(null)
    const [classyCampaign, setClassyCampaign] = useState<any>(null)
    const [classyAutogenerateAmbassadorTeams, setClassyAutogenerateAmbassadorTeams] = useState<boolean>(false)
    const [useClassyFundraiserPage, setUseClassyFundraiserPage] = useState<boolean>(false)
    const [personalFundraisingCampaign, setPersonalFundraisingCampaign] = useState<FundraisingCampaign | null>(null)

    const [introVideoFileAdded, setIntroVideoFileAdded] = useState<boolean>(false)

    const [invalidName, setInvalidName] = useState<boolean>(false)
    const [invalidType, setInvalidType] = useState<boolean>(false)
    const [invalidTextMessageTemplate, setInvalidTextMessageTemplate] = useState<boolean>(false)

    const dispatch = useAppDispatch()

    const cognitoData = useAppSelector(state => state.authAccount.cognitoData)
    const organization = useAppSelector(state => state.organization)
    const selectedCampaign = useAppSelector(state => state.fundraising.selectedCampaign)
    const selectedStaffProfile = useAppSelector(state => state.authAccount.selectedStaffProfile)

    function handleClose() {
        setId(null)
        setType(null)
        setName(null)
        setStaffLead(null)
        setCampaignEndDate(null)
        setIntroVideoFile(null)
        setIntroVideoURI(null)
        setIntroVideoFileAdded(false)
        setTextMessageTemplate(null)
        setInitialTextReminderTemplate(null)
        setPhoneCallScript(null)
        setFollowUpYesMessageTemplate(null)
        setFollowUpNoMessageTemplate(null)
        setFollowUpReminderTemplate(null)
        setInvalidName(false)
        setInvalidType(false)
        setCampaignGoal(0)
        setGivingCircleBaseAmount("")
        setGivingCircleTierOneMultiplier("")
        setGivingCircleTierTwoMultiplier("")
        setGivingCircleTierThreeMultiplier("")
        setGivingCircleTierFourMultiplier("")
        setPerAmbassadorGoal(0)
        setPerContactDonationGoal(0)
        setInvalidTextMessageTemplate(false)
        setAmbassadorSpecificCtaUrlEnabled(false)
        setAmbassadorCanUploadCtaUrl(false)
        setAmbassadorSpecificVideoUrlEnabled(false)
        setAmbassadorCanUploadVideoUrl(false)
        setCrowbarVideoURL(null)
        setCallToActionURL(null)
        setClassyAutogenerateAmbassadorTeams(false)
        setUseClassyCtaURL(false)
        setClassyDonationPageHostUrL(null)
        setRecruitmentForCampaign(null)
        setPersonalFundraisingCampaign(null)
        setUseClassyFundraiserPage(false)
        // dispatch(clearSelectedCampaign(null))
        props.onClose()
    }

    useEffect(() => {
        (async () => {
            if (selectedCampaign != null && window.location.pathname == '/admin/fundraising/campaigns/manage') {
                if (selectedCampaign.id) setId(selectedCampaign.id)
                if (selectedCampaign.type) setType(selectedCampaign.type)
                if (selectedCampaign.campaignName) setName(selectedCampaign.campaignName)
                if (selectedCampaign.staffLeadID) {
                    const { data }: any = await API.graphql({
                        query: getStaffProfile,
                        variables: {
                            id: selectedCampaign.staffLeadID
                        }
                    })
                    setStaffLead(data.getStaffProfile)
                }
                if (selectedCampaign.introVideoURI) {
                    setIntroVideoURI(selectedCampaign.introVideoURI)
                    const uri = await Storage.get(selectedCampaign.introVideoURI)
                    console.log(uri)
                    setIntroVideoURI(uri)
                    setIntroVideoFileAdded(true)
                }
                if (selectedCampaign.textMessageTemplate) setTextMessageTemplate(selectedCampaign.textMessageTemplate)
                if (selectedCampaign.initialMessageReminderTemplate) setInitialTextReminderTemplate(selectedCampaign.initialMessageReminderTemplate)
                if (selectedCampaign.phoneCallScriptTemplate) setPhoneCallScript(selectedCampaign.phoneCallScriptTemplate)
                if (selectedCampaign.phoneCallScriptTemplate) setPhoneCallScriptRTE(selectedCampaign.phoneCallScriptTemplate ? EditorValue.createFromString(selectedCampaign.phoneCallScriptTemplate, 'html') : EditorValue.createEmpty())
                if (selectedCampaign.followUpYesMessageTemplate) setFollowUpYesMessageTemplate(selectedCampaign.followUpYesMessageTemplate)
                if (selectedCampaign.followUpNoMessageTemplate) setFollowUpNoMessageTemplate(selectedCampaign.followUpNoMessageTemplate)
                if (selectedCampaign.followUpReminderMessageTemplate) setFollowUpReminderTemplate(selectedCampaign.followUpReminderMessageTemplate)
                if (selectedCampaign.campaignGoal) setCampaignGoal(selectedCampaign.campaignGoal)
                if (selectedCampaign.givingCircleBaseAmount) setGivingCircleBaseAmount(selectedCampaign.givingCircleBaseAmount.toString())
                if (selectedCampaign.perAmbassadorGoal) setPerAmbassadorGoal(selectedCampaign.perAmbassadorGoal)
                if (selectedCampaign.perContactDonationGoal) setPerContactDonationGoal(selectedCampaign.perContactDonationGoal)
                if (selectedCampaign.campaignEndDate) setCampaignEndDate(selectedCampaign.campaignEndDate)
                if (selectedCampaign.ctaURL) setCallToActionURL(selectedCampaign.ctaURL)
                if (selectedCampaign.crowbarVideoURL) setCrowbarVideoURL(selectedCampaign.crowbarVideoURL)
                if (selectedCampaign.ambassadorSpecificCtaUrlEnabled) setAmbassadorSpecificCtaUrlEnabled(selectedCampaign.ambassadorSpecificCtaUrlEnabled)
                if (selectedCampaign.ambassadorCanUploadCtaUrl) setAmbassadorCanUploadCtaUrl(selectedCampaign.ambassadorCanUploadCtaUrl)
                if (selectedCampaign.ambassadorSpecificVideoUrlEnabled) setAmbassadorSpecificVideoUrlEnabled(selectedCampaign.ambassadorSpecificVideoUrlEnabled)
                if (selectedCampaign.ambassadorCanUploadVideoUrl) setAmbassadorCanUploadVideoUrl(selectedCampaign.ambassadorCanUploadVideoUrl)
                if (selectedCampaign.classyCampaignID) {
                    // await dispatch(getClassyCampaigns())
                    setClassyCampaign(selectedCampaign.classyCampaignID)
                }
                if (selectedCampaign.classyAutogenerateAmbassadorTeams) setClassyAutogenerateAmbassadorTeams(selectedCampaign.classyAutogenerateAmbassadorTeams)
                if (selectedCampaign.useClassyCtaURL) setUseClassyCtaURL(selectedCampaign.useClassyCtaURL)
                if (selectedCampaign.classyDonationPageHostURL) setClassyDonationPageHostUrL(selectedCampaign.classyDonationPageHostURL)
                if (selectedCampaign.recruitmentForCampaign) setRecruitmentForCampaign(selectedCampaign.recruitmentForCampaign)
                if (selectedCampaign.personalFundraisingCampaign) setPersonalFundraisingCampaign(selectedCampaign.personalFundraisingCampaign)
                if (selectedCampaign.useClassyFundraiserPage) setUseClassyFundraiserPage(selectedCampaign.useClassyFundraiserPage)

                // giving circle
                if (selectedCampaign.givingCircleIncrements) {
                    if (selectedCampaign.givingCircleIncrements[0]) setGivingCircleTierOneMultiplier(selectedCampaign.givingCircleIncrements[0].toString())
                    if (selectedCampaign.givingCircleIncrements[1]) setGivingCircleTierTwoMultiplier(selectedCampaign.givingCircleIncrements[1].toString())
                    if (selectedCampaign.givingCircleIncrements[2]) setGivingCircleTierThreeMultiplier(selectedCampaign.givingCircleIncrements[2].toString())
                    if (selectedCampaign.givingCircleIncrements[3]) setGivingCircleTierFourMultiplier(selectedCampaign.givingCircleIncrements[3].toString())
                }
            }
            if (props.visible && selectedCampaign && organization && organization.activeClassyConnection == null) {
                if (cognitoData) {
                    await dispatch(getActiveClassyConnections())
                } else {
                    await dispatch(reloadAuthAccount())
                    await dispatch(getActiveClassyConnections())
                }
            }
        })()
    }, [selectedCampaign, props.visible])

    const onDrop = useCallback(async acceptedFiles => {
        // Do something with the files
        console.log(acceptedFiles[0])
        if (selectedCampaign) {
            try {
                setIntroVideoFile(acceptedFiles[0])
            } catch (err) {
                console.log(err)
            }
        }
    }, [])

    const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({ maxFiles: 1, accept: 'video/mp4,video/mov', onDrop: onDrop, maxSize: 104857600 });

    return (
        <Dialog open={props.visible} onClose={() => {
            handleClose()
        }} maxWidth="lg" fullWidth sx={{ zIndex: 99 }}>
            <div className="d-grid col p-5 pb-0">
                <div className="row">
                    <Typography variant="h3" sx={{ padding: 0 }}>Add a new campaign</Typography>
                </div>
            </div>
            <div className="row p-5" style={{ backgroundColor: COLORS.WHITE }}>
                <div className="col-12 col-lg-6 py-3">
                    <Select
                        value={type}
                        onChange={(e) => setType(e.target.value as FundraisingCampaignType)}
                        fullWidth
                        label="Campagin type"
                    >
                        <MenuItem value={FundraisingCampaignType.TenX}>10x</MenuItem>
                        <MenuItem value={FundraisingCampaignType.GivingCircle}>Giving Circle</MenuItem>
                        <MenuItem value={FundraisingCampaignType.AmbassadorRecruiting}>Ambassador Recruitment</MenuItem>
                    </Select>
                </div>
                <div className="col-12 col-lg-6 py-3">
                    <TextField
                        variant='outlined'
                        type="text"
                        id="name"
                        placeholder="Campaign name"
                        value={name}
                        onChange={e => {
                            setName(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                        error={invalidName}
                        helperText={invalidName ? "Please enter a campaign name" : ''}
                        label="Campaign name"
                    />
                </div>
                {
                    type == FundraisingCampaignType.AmbassadorRecruiting &&
                    <div className="col-12 col-lg-6 py-3">
                        <FundraisingCampaignSelector label="Campaign Recruiting For" className="w-100" value={recruitmentForCampaign} onChange={(e) => setRecruitmentForCampaign(e)} />
                    </div>
                }
                <div className="col-lg-6 col-sm-12 py-3">
                    <StaffSelector fullWidth value={staffLead} onChange={(e) => { setStaffLead(e) }} label="Campaign lead" />
                </div>
                <div className="col-lg-6 col-sm-12 py-3">
                    <TextField
                        type="date"
                        id="name"
                        InputLabelProps={{ shrink: true }}
                        value={campaignEndDate}
                        onChange={e => {
                            setCampaignEndDate(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                        label="Campaign end date"
                    />
                </div>
                <div className="col-lg-6 col-sm-12 py-3">
                    <TextField
                        type="number"
                        id="name"
                        label="Campaign goal"
                        value={campaignGoal}
                        onChange={e => {
                            setCampaignGoal(Number(e.target.value))
                        }}
                        sx={{ width: '100%' }}
                        helperText={type == FundraisingCampaignType.AmbassadorRecruiting ? `(# of ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverridePlural ? selectedStaffProfile.organization.ambassadorTermOverridePlural : 'Ambassador'})` : type == FundraisingCampaignType.TenX ? '($ raised)' : type == FundraisingCampaignType.GivingCircle ? '(# of new monthly recurring donors)' : ''}
                    />
                </div>
                <div className="col-lg-6 col-sm-12 py-3">
                    <TextField
                        type="number"
                        id="name"
                        label={`Goal per ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'}`}
                        value={perAmbassadorGoal}
                        onChange={e => {
                            setPerAmbassadorGoal(Number(e.target.value))
                        }}
                        sx={{ width: '100%' }}
                        helperText={type == FundraisingCampaignType.AmbassadorRecruiting ? `(# of ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverridePlural ? selectedStaffProfile.organization.ambassadorTermOverridePlural : 'Ambassador'} recruited per ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'})` : type == FundraisingCampaignType.TenX ? `($ raised per ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'})` : type == FundraisingCampaignType.GivingCircle ? `(# of new monthly recurring donors added per ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'})` : ''}
                    />
                </div>
                {
                    type == FundraisingCampaignType.TenX &&
                    <div className="col-12 col-lg-6 py-3">
                        <TextField
                            label="Target donation amount per contact"
                            type="number"
                            id="name"
                            value={perContactDonationGoal}
                            onChange={e => {
                                setPerContactDonationGoal(Number(e.target.value))
                            }}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            sx={{ width: '100%' }}
                        />
                    </div>
                }
                {
                    type == FundraisingCampaignType.GivingCircle &&
                    <div className="col-12 col-lg-6 pt-3">
                        <TextField
                            type="number"
                            id="name"
                            label="Base Giving Circle amount"
                            value={givingCircleBaseAmount}
                            onChange={e => {
                                if (Number(e.target.value) || e.target.value == '') {
                                    setGivingCircleBaseAmount(e.target.value)
                                }
                            }}
                            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            sx={{ width: '100%' }}
                        />
                    </div>
                }
                {
                    type == FundraisingCampaignType.GivingCircle && givingCircleBaseAmount &&
                    <div className="col-12 col-lg-6 pt-3 d-flex flex-row justify-content-center align-items-center">
                        <Typography variant="caption" className="mx-3">Increments</Typography>
                        <ToggleButtonGroup className="w-100" fullWidth>
                            <ToggleButton disabled value="option1"><TextField variant="outlined" label="Base" fullWidth value={givingCircleTierOneMultiplier} InputProps={{ startAdornment: <InputAdornment position="end"><Typography variant="caption" mr="10px">x</Typography></InputAdornment> }} /></ToggleButton>
                            <ToggleButton value="option2"><TextField variant="outlined" label="Tier one" fullWidth value={givingCircleTierTwoMultiplier} onChange={(e) => { if (Number(e.target.value) || e.target.value == '') setGivingCircleTierTwoMultiplier(e.target.value) }} InputProps={{ startAdornment: <InputAdornment position="end"><Typography variant="caption" mr="10px">x</Typography></InputAdornment> }} /></ToggleButton>
                            <ToggleButton value="option3"><TextField variant="outlined" label="Tier two" fullWidth value={givingCircleTierThreeMultiplier} onChange={(e) => { if (Number(e.target.value) || e.target.value == '') setGivingCircleTierThreeMultiplier(e.target.value) }} InputProps={{ startAdornment: <InputAdornment position="end"><Typography variant="caption" mr="10px">x</Typography></InputAdornment> }} /></ToggleButton>
                            <ToggleButton value="option4"><TextField variant="outlined" label="Tier three" fullWidth value={givingCircleTierFourMultiplier} onChange={(e) => { if (Number(e.target.value) || e.target.value == '') setGivingCircleTierFourMultiplier(e.target.value) }} InputProps={{ startAdornment: <InputAdornment position="end"><Typography variant="caption" mr="10px">x</Typography></InputAdornment> }} /></ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                }
                {
                    type == FundraisingCampaignType.GivingCircle && givingCircleBaseAmount &&
                    <div className="col-12 col-lg-6 d-flex flex-column">
                        <FormControlLabel checked={givingCircleAllowCustomAmounts} onChange={(e, checked) => setGivingCircleAllowCustomAmounts(checked)} control={<Checkbox />} label="Allow custom donation amount" />
                    </div>
                }
                {
                    type == FundraisingCampaignType.GivingCircle && givingCircleBaseAmount &&
                    <div className="col-12 py-3 d-flex flex-column">
                        <Typography variant="caption">Amount preview</Typography>
                        <ToggleButtonGroup className="w-100" fullWidth>
                            <ToggleButton value="1x">${Number(givingCircleBaseAmount).toFixed(0)}</ToggleButton>
                            <ToggleButton value="2x">${(Number(givingCircleBaseAmount) * (givingCircleTierTwoMultiplier ? Number(givingCircleTierTwoMultiplier) : 2)).toFixed(0)}</ToggleButton>
                            <ToggleButton value="4x">${(Number(givingCircleBaseAmount) * (givingCircleTierThreeMultiplier ? Number(givingCircleTierThreeMultiplier) : 4)).toFixed(0)}</ToggleButton>
                            <ToggleButton value="10x">${(Number(givingCircleBaseAmount) * (givingCircleTierFourMultiplier ? Number(givingCircleTierFourMultiplier) : 10)).toFixed(0)}</ToggleButton>
                            {
                                givingCircleAllowCustomAmounts &&
                                <ToggleButton value="option4"><TextField variant="standard" label="Custom" fullWidth InputProps={{ startAdornment: <InputAdornment position="end"><Typography variant="caption" mr="10px">$</Typography></InputAdornment> }} /></ToggleButton>
                            }
                        </ToggleButtonGroup>
                    </div>
                }
                {
                    (type == FundraisingCampaignType.TenX || type == FundraisingCampaignType.AmbassadorRecruiting) &&
                    <div className="col-12 col-lg-6" />
                }
                {
                    (useClassyCtaURL == null || useClassyCtaURL == false) &&
                    <div className="col-12 col-lg-6">
                        <div className="p-3 border rounded bg-light">
                            <FormControlLabel checked={ambassadorSpecificCtaUrlEnabled} onChange={(e, checked) => setAmbassadorSpecificCtaUrlEnabled(checked)} control={<Switch />} label={`Enable ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} specific call-to-action url's`} />
                            {
                                !ambassadorSpecificCtaUrlEnabled ?
                                    <TextField
                                        type="text"
                                        id="name"
                                        label={type == FundraisingCampaignType.AmbassadorRecruiting ? 'General Call to action URL' : 'Donation Page Link (leave blank for auto-generated Latent URL)'}
                                        value={callToActionURL}
                                        onChange={e => {
                                            setCallToActionURL(e.target.value)
                                        }}
                                        sx={{ width: '100%' }}
                                        className="my-2"
                                        helperText={type == FundraisingCampaignType.AmbassadorRecruiting ? `If every potential user you are recruiting will sign up using the same URL then enter that URL here. Otherwise if each ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} will have a different link then enable this option.` : "If you are using an external payment processer and every donation will go through the same donation page, put the URL for that page here. Otherwise, if you are using Latent as your payment processor, this link will be generated automatically and you can leave this field blank."}
                                    />
                                    :
                                    <FormControlLabel className="my-2" checked={ambassadorCanUploadCtaUrl} onChange={(e, checked) => setAmbassadorCanUploadCtaUrl(checked)} control={<Switch />} label={`${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} has access to upload URL through the Latent mobile app.`} />
                            }
                        </div>
                    </div>
                }
                <div className="col-12 col-lg-6">
                    <div className="p-3 border rounded bg-light">
                        <FormControlLabel checked={ambassadorSpecificVideoUrlEnabled} onChange={(e, checked) => setAmbassadorSpecificVideoUrlEnabled(checked)} control={<Switch />} label={`Enable ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} specific Crowbar Video™`} />
                        {
                            !ambassadorSpecificVideoUrlEnabled ?
                                <TextField
                                    type="text"
                                    id="name"
                                    label="General Crowbar Video™ Url (optional)"
                                    value={crowbarVideoURL}
                                    onChange={e => {
                                        setCrowbarVideoURL(e.target.value)
                                    }}
                                    sx={{ width: '100%' }}
                                    className="my-2"
                                    helperText={`If every ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} will be uploading their own video then enable this option. Otherwise enter the URL of the general Crowbar Video™ for this Campaign.`}
                                />
                                :
                                <div className="d-flex flex-column justify-content-center align-items-start">
                                    <FormControlLabel className="my-2" checked={ambassadorCanUploadVideoUrl} onChange={(e, checked) => setAmbassadorCanUploadVideoUrl(checked)} control={<Switch />} label={`${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} has access to upload URL through the Latent mobile app.`} />
                                    <TextField
                                        type="text"
                                        id="name"
                                        label="Backup Crowbar Video™ URL"
                                        value={crowbarVideoURL}
                                        onChange={e => {
                                            setCrowbarVideoURL(e.target.value)
                                        }}
                                        sx={{ width: '100%' }}
                                        className="my-2"
                                        helperText={`This URL will be used if the ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} doesn't have a specific URL attached to their account.`}
                                    />
                                </div>
                        }
                    </div>
                </div>
                {
                    organization && organization.activeClassyConnection && organization.activeClassyConnection.length > 0 &&
                    <div className="col-12 my-3">
                        <div className="d-flex flex-column justify-content-center align-items-start border rounded p-3 w-100">
                            <div className="row w-100">
                                <div className="col-12 col-lg-6 h-auto d-flex flex-row justify-content-start align-items-center">
                                    <Typography variant="h5">Classy Options</Typography>
                                </div>
                                <div className="col-12 col-lg-6 d-flex flex-row justify-content-start justify-content-lg-end align-items-center h-auto">
                                    <FormControlLabel checked={useClassyCtaURL} onChange={(e, checked) => setUseClassyCtaURL(checked)} control={<Switch />} label={"Use Classy Donation Pages"} />
                                </div>
                                {
                                    useClassyCtaURL &&
                                    <div className="col-12 my-2">
                                        <TextField label="Home Page Classy URL" value={classyDonationPageHostURL} onChange={(e) => setClassyDonationPageHostUrL(e.target.value)} fullWidth helperText="If you have changed your Classy URL from the standard 'https://classy.org' then please specify your new Classy URL. Otherwise all donation links will default to the standard Classy URL." />
                                    </div>
                                }
                                {
                                    useClassyCtaURL &&
                                    <div className="col-12 d-flex flex-row justify-content-start align-items-center my-2 h-auto">
                                        <FormControlLabel checked={useClassyFundraiserPage} onChange={(e, checked) => setUseClassyFundraiserPage(checked)} control={<Switch />} label={"Call-to-action URL should link to a Campaign-based Personal Fundraiser page instead of a Campaign-based Team Page"} />
                                    </div>
                                }
                                {
                                    useClassyCtaURL && useClassyFundraiserPage == false &&
                                    <div className="col-12 col-lg-6 my-2 h-auto">
                                        <div className="d-flex flex-column justify-content-center align-items-start h-100">
                                            <div className="d-flex flex-row justify-content-center align-items-center mb-2">
                                                <Typography>Classy Connected Campaign</Typography>
                                                <Tooltip title="The Classy Connected Campaign is the Classy Campaign you want to link to this Latent Campaign. Linking your Classy Campaign will give you the ability to use Classy Fundraising Pages to process donations instead of the built-in Latent Campaign Pages.">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </div>
                                            <ClassyCampaignSelector label="Classy Campaign" value={classyCampaign && classyCampaign.id ? classyCampaign : classyCampaign ? { id: classyCampaign } : null} onChange={(e) => { setClassyCampaign(e) }} className="w-100" />
                                        </div>
                                    </div>
                                }
                                {
                                    useClassyCtaURL && useClassyFundraiserPage == false &&
                                    <div className="col-12 col-lg-6 my-2 h-auto">
                                        <div className="d-flex flex-column justify-content-center align-items-start w-100">
                                            <div className="d-flex flex-row justify-content-center align-items-center mb-2">
                                                <Typography>Classy Page Creation</Typography>
                                                <Tooltip title={`The "Autocreate Classy Pages" option will automatically generate a new Fundraising Team for each ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} that is added to this Recruitment Campaign. We recommend this option because you won't have to manage separate Classy Pages on your own. If you need more customization for your Classy Pages or if you've already created Classy Pages for each ${selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'} then select the "Manually Assign Classy Pages" option.`}>
                                                    <InfoIcon />
                                                </Tooltip>
                                            </div>
                                            <ToggleButtonGroup
                                                value={classyAutogenerateAmbassadorTeams ? 'auto' : 'manual'}
                                                onChange={(e, value) => {
                                                    console.log(value)
                                                    if (value != null) {
                                                        if (value == 'auto') setClassyAutogenerateAmbassadorTeams(true)
                                                        else setClassyAutogenerateAmbassadorTeams(false)
                                                    }
                                                }}
                                                exclusive
                                            >
                                                <ToggleButton value="auto">
                                                    <Typography>Autocreate Classy Pages</Typography>
                                                </ToggleButton>
                                                <ToggleButton value="manual">
                                                    <Typography>Manually Assign Classy Pages</Typography>
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </div>
                                    </div>
                                }
                                {
                                    useClassyCtaURL &&
                                    <div className="col-12 col-lg-6 my-2 h-auto">
                                        <div className="d-flex flex-column justify-content-center align-items-start w-100">
                                            <FundraisingCampaignSelector label="Latent Personal Fundraising Campaign" className="w-100" value={personalFundraisingCampaign} onChange={(e) => setPersonalFundraisingCampaign(e)} />
                                        </div>
                                    </div>
                                }
                                {
                                    useClassyCtaURL && useClassyFundraiserPage == true &&
                                    <Typography variant="body1">When using Classy Personal Fundraiser pages, you'll need to specify the Classy Supporter ID for each {selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.ambassadorTermOverrideSingular ? selectedStaffProfile.organization.ambassadorTermOverrideSingular : 'Ambassador'}</Typography>
                                }
                            </div>
                        </div>
                    </div>
                }
                {/* <div className="col-lg-12 py-3">
                    <Typography variant="body1" sx={{ padding: 0 }}>Intro Video</Typography><br />
                    {
                        introVideoFileAdded ?
                            <div className="d-flex flex-row justify-content-start align-items-center">
                                {
                                    introVideoURI ?
                                        <video style={{ height: 100, width: 'auto' }} src={introVideoURI} />
                                        :
                                        <Typography variant="body1">video uploaded</Typography>
                                }
                                <Button onClick={() => { setIntroVideoFileAdded(false); setIntroVideoURI(null); setIntroVideoFile(null) }} variant="outlined" className="mx-2">Upload new file</Button>
                            </div>
                            :
                            <input
                                id="introVideoFile"
                                type="file"
                                name="introVideo"
                                accept=".mp4,.mov"
                                placeholder="Upload your intro video (MP4)"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length == 1) {
                                        setIntroVideoFile(e.target.files[0])
                                        setIntroVideoFileAdded(true)
                                        console.log(e.target.files[0])
                                    }
                                }}
                                className="my-2"
                            />
                    }
                </div> */}
                <div className="col-lg-12 mb-3 mt-5">
                    <div className="p-3 border rounded d-flex flex-column justify-content-center-align-items-start">
                        <Typography variant="h5">Available Merge Fields</Typography>
                        <Typography variant="body1">$firstName - First name of the contact</Typography>
                        <Typography variant="body1">$lastName - Last name of the contact</Typography>
                        <Typography variant="body1">$url - Call-to-action URL</Typography>
                        <Typography variant="body1">$videoUrl - Crowbar Video™ URL</Typography>
                        <Typography variant="body1">$endDate - End date of the campaign</Typography>
                    </div>
                </div>
                <div className="col-lg-12 py-3">
                    <TextField
                        label="Text Message Template"
                        type="text"
                        id="textMessageTemplate"
                        multiline
                        minRows={3}
                        placeholder="The initial text message the person raising funds will send to their contacts."
                        value={textMessageTemplate}
                        onChange={e => {
                            setTextMessageTemplate(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                        error={invalidTextMessageTemplate}
                        helperText={invalidTextMessageTemplate ? "Please enter something in this field. The initial text is required for the campaign process." : ''}
                    />
                </div>
                <div className="col-lg-12 py-3">
                    <TextField
                        label="Initial Message Reminder Template"
                        type="text"
                        id="initialMessageReminderTemplate"
                        multiline
                        minRows={3}
                        placeholder="A reminder message for contacts who haven't responded to the initial text message."
                        value={initialTextReminderTemplate}
                        onChange={e => {
                            setInitialTextReminderTemplate(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                    />
                </div>
                <div className="col-lg-12 py-3">
                    <Typography variant="body1" sx={{ padding: 0 }}>Phone Call Script</Typography><br />
                    <RichTextEditor
                        value={phoneCallScriptRTE ? phoneCallScriptRTE : EditorValue.createEmpty()}
                        onChange={(e) => {
                            setPhoneCallScriptRTE(e)
                        }}
                        rootStyle={{ minHeight: '150px', fontFamily: 'ProximaNova' }}
                        placeholder="The script the person raising funds will use when calling their contacts."
                    />
                    {/* <TextField
                        type="text"
                        id="phoneCallScript"
                        multiline
                        minRows={3}
                        placeholder="The script the person raising funds will use when calling their contacts."
                        value={phoneCallScript}
                        onChange={e => {
                            setPhoneCallScript(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                    /> */}
                </div>
                <div className="col-lg-12 py-3">
                    <TextField
                        label="Follow Up YES Text Message Template"
                        type="text"
                        id="followUpMessageTemplate"
                        multiline
                        minRows={3}
                        placeholder="The follow up text message the person raising funds will send to their contacts."
                        value={followUpYesMessageTemplate}
                        onChange={e => {
                            setFollowUpYesMessageTemplate(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                    />
                </div>
                <div className="col-lg-12 py-3">
                    <TextField
                        label="Follow Up NO Text Message Template"
                        type="text"
                        id="followUpMessageTemplate"
                        multiline
                        minRows={3}
                        placeholder="The follow up text message the person raising funds will send to their contacts."
                        value={followUpNoMessageTemplate}
                        onChange={e => {
                            setFollowUpNoMessageTemplate(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                    />
                </div>
                <div className="col-lg-12 py-3">
                    <TextField
                        label="Follow Up Message Reminder Template"
                        type="text"
                        id="followUpReminderMessageTemplate"
                        multiline
                        minRows={3}
                        placeholder="A reminder message for contacts who haven't responded to the initial text message."
                        value={followUpReminderTemplate}
                        onChange={e => {
                            setFollowUpReminderTemplate(e.target.value)
                        }}
                        sx={{ width: '100%' }}
                    />
                </div>
            </div>
            <DialogActions style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                <Button onClick={handleClose} size="large" variant="outlined" sx={{ width: '25%' }} >Cancel</Button>
                <Button
                    onClick={async () => {
                        if (name == '') {
                            setInvalidName(true)
                        } else {
                            setInvalidName(false)
                        }
                        if (type == null) {
                            setInvalidType(true)
                        } else {
                            setInvalidType(false)
                        }
                        if (textMessageTemplate == '') {
                            setInvalidTextMessageTemplate(true)
                        } else {
                            setInvalidTextMessageTemplate(false)
                        }
                        if (name != null && name != '' && type != null && textMessageTemplate != null && textMessageTemplate != '') {

                            const givingCircleTiers = []

                            if (type == FundraisingCampaignType.GivingCircle) {
                                givingCircleTiers.push(Number(givingCircleTierOneMultiplier))
                                if (Number(givingCircleTierTwoMultiplier)) givingCircleTiers.push(Number(givingCircleTierTwoMultiplier))
                                if (Number(givingCircleTierThreeMultiplier)) givingCircleTiers.push(Number(givingCircleTierThreeMultiplier))
                                if (Number(givingCircleTierFourMultiplier)) givingCircleTiers.push(Number(givingCircleTierFourMultiplier))
                            }

                            console.log(phoneCallScriptRTE)

                            const createNewCampaignResponse = await dispatch(createBackendFundraisingCampaign({
                                id,
                                type,
                                name,
                                staffLead,
                                introVideoFile,
                                textMessageTemplate,
                                initialTextReminderTemplate,
                                phoneCallScriptTemplate: phoneCallScriptRTE && phoneCallScriptRTE.toString('html') != '<p><br></p>' ? phoneCallScriptRTE.toString('html') : null,
                                followUpMessageTemplate: null,
                                followUpYesMessageTemplate,
                                followUpNoMessageTemplate,
                                followUpReminderTemplate,
                                campaignEndDate,
                                campaignGoal,
                                perAmbassadorGoal,
                                perContactDonationGoal,
                                givingCircleBaseAmount,
                                givingCircleIncrements: givingCircleTiers,
                                givingCircleAllowCustomAmounts,
                                ctaURL: callToActionURL,
                                crowbarVideoURL,
                                ambassadorSpecificCtaUrlEnabled,
                                ambassadorCanUploadCtaUrl,
                                ambassadorSpecificVideoUrlEnabled,
                                ambassadorCanUploadVideoUrl,
                                recruitmentForCampaignID: recruitmentForCampaign ? recruitmentForCampaign.id : null,
                                useClassyCtaURL,
                                classyDonationPageHostURL,
                                classyCampaignID: classyCampaign && classyCampaign.id ? classyCampaign.id : classyCampaign ? classyCampaign : null,
                                classyAutogenerateAmbassadorTeams: classyCampaign ? classyAutogenerateAmbassadorTeams : null,
                                personalFundraisingCampaignID: personalFundraisingCampaign ? personalFundraisingCampaign.id : null,
                                useClassyFundraiserPage
                            }))

                            console.log('create new campaign response: ', createNewCampaignResponse)

                            if (createNewCampaignResponse.payload) {
                                dispatch(getFundraisingCampaigns())
                                handleClose()
                                props.onClose()
                            } else {
                                alert('An error occurred. Try again in a little bit or contact support.')
                            }

                        }
                    }}
                    size="large"
                    variant="contained"
                    sx={{ width: '25%' }}
                >
                    {id ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}